import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/Elements/Button";
import { emailIdrRegEx } from "../../utils";
import { success, error } from "../../components/notify";
import { setProgressData } from "../../store/progressData/progressDataSlice";
import { useProgress } from "../../hooks/useProgress";
import DrawerHeader from "../Header/DrawerHeader";
import Layout from "../../layout/Layout";
import ReCAPTCHA from "react-google-recaptcha";
import OtpInput from "../Elements/OtpInput";
import { Edit } from "../Icon/Edit";

const AccountDeleteModalTwo = ({ className, onClose, fromWhere = "" }) => {
  const dispatch = useDispatch();
  const { progressFunction } = useProgress();
  const websiteColor = useSelector((state) => state?.onLoadData?.color);

  // State management
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [validationError, setValidationError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [intervalData, setIntervalData] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [step, setStep] = useState(1);
  const [showResend, setShowResend] = useState(false);
  const [counterCount, setCounterCount] = useState(1);
  const [timer, setTimer] = useState("20");
  const [otp, setOtp] = useState("");
  const timerRef = useRef(null);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (intervalData) clearInterval(intervalData);
    };
  }, [intervalData]);

  // Timer logic
  const startTimer = (duration) => {
    let timeLeft = duration;
    setTimer("20");

    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      timeLeft -= 1;

      const minutes = Math.floor(timeLeft / 60);
      const seconds = timeLeft % 60;
      setTimer(
        `${minutes.toString().padStart(2, "0")}:${seconds
          .toString()
          .padStart(2, "0")}`
      );

      if (timeLeft <= 0) {
        clearInterval(timerRef.current);
        setShowResend(true);
      }
    }, 1000);
  };

  const validateInput = () => {
    const input = emailOrPhone.trim();

    if (!input) {
      setValidationError("This field is required");
      return false;
    }

    const isEmail = isNaN(input);
    if (isEmail && !emailIdrRegEx.test(input)) {
      setValidationError("Invalid email address");
      return false;
    }

    if (!isEmail && input.length !== 11) {
      setValidationError("Invalid phone number (must be 11 digits)");
      return false;
    }

    if (!captchaToken) {
      setValidationError("Please complete the CAPTCHA");
      return false;
    }

    setValidationError(null);
    return true;
  };

  const handleEmailOrPhoneChange = (event) => {
    const value = event.target.value;
    if (value.length <= 50) {
      setEmailOrPhone(value);
      setValidationError(null);
    }
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    setValidationError(null);
  };

  const handleResendOtp = async (via = "SMS") => {
    setIsLoading(true);
    setTimeout(() => {
      success(`OTP resent via ${via}`);
      setCounterCount((prev) => prev + 1);
      startTimer(20);
      setShowResend(false);
      setIsLoading(false);
    }, 1000);
  };

  const handleAccountDelete = async () => {
    if (step === 1) {
      if (!validateInput()) return;

      setIsLoading(true);
      setTimeout(() => {
        success("OTP sent successfully");
        setStep(2);
        startTimer(20);
        setIsLoading(false);
      }, 1500);
    } else {
      if (otp.length !== 4) {
        error("Please enter a valid 4-digit OTP");
        return;
      }

      setIsLoading(true);
      setTimeout(() => {
        success("Your account has been deleted successfully");
        setIsLoading(false);
        onClose?.();
      }, 1500);
    }
  };

  const handleOtpChange = (otpValue) => {
    setOtp(otpValue);
  };

  const renderStepOne = () => (
    <>
      <label className="block mb-2 text-sm">
        Enter Email/Phone <span className="text-primary">*</span>
      </label>
      <input
        value={emailOrPhone}
        onChange={handleEmailOrPhoneChange}
        type="text"
        className="bg-white-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:border-blue-500 focus:outline-none block w-full ps-3.5 p-2.5"
        placeholder="Enter Email/Phone"
        autoComplete="off"
      />
      {validationError && (
        <span className="text-primary text-sm">{validationError}</span>
      )}
      <ReCAPTCHA
        sitekey={import.meta.env.VITE_CAPTCHA_SITE_KEY || "dummy-key"}
        onChange={handleCaptchaChange}
        className="mt-4"
      />
      <Button
        onClick={handleAccountDelete}
        disabled={isLoading}
        className="text-white w-full mt-5 rounded-lg text-sm inline-flex justify-center items-center px-5 py-2.5"
        style={{ backgroundColor: websiteColor }}
      >
        {isLoading ? "Processing..." : "Delete Account"}
      </Button>
    </>
  );

  const renderStepTwo = () => (
    <>
      <div className="mb-4">
        <span className="text-sm">
          OTP sent to
          <span className="flex space-x-2 items-center mt-2">
            <span className="font-medium">{emailOrPhone}</span>
            <Button
              onClick={() => {
                setStep(1);
                setCounterCount(1);
                setOtp("");
                setShowResend(false);
                if (timerRef.current) clearInterval(timerRef.current);
              }}
              className="text-xs px-2 py-1"
            >
              <Edit />
            </Button>
          </span>
        </span>
      </div>

      <div className="pt-2">
        <OtpInput
          length={4}
          onOtpSubmit={handleOtpChange}
          isDisabled={isLoading}
        />
      </div>

      {showResend ? (
        <div className="flex justify-between pt-4">
          <Button
            onClick={() => handleResendOtp("SMS")}
            disabled={isLoading}
            className="w-[131px] sm:w-[156px] h-11 px-4 py-1 bg-pink-50 rounded-lg flex justify-center items-center gap-1"
          >
            Resend OTP
          </Button>
          <Button
            onClick={() => handleResendOtp("VOICE")}
            disabled={isLoading}
            className="w-[131px] sm:w-[156px] h-11 px-4 py-1 bg-pink-50 rounded-lg flex justify-center items-center gap-1"
          >
            Voice OTP
          </Button>
        </div>
      ) : (
        <div className="text-center mt-4 md:mt-6">
          <span className="text-p6 font-bold">{timer}</span>
        </div>
      )}

      <Button
        onClick={handleAccountDelete}
        disabled={isLoading || otp.length !== 4}
        className="text-white w-full mt-5 rounded-lg text-sm inline-flex justify-center items-center px-5 py-2.5"
        style={{ backgroundColor: websiteColor }}
      >
        {isLoading ? "Verifying..." : "Confirm Delete"}
      </Button>
    </>
  );

  return fromWhere === "footer" ? (
    <div className={`w-full md:w-[448px] ${className} pb-12 mdl:pb-8`}>
      <DrawerHeader
        title="Account Delete"
        onClose={onClose}
        description={
          step === 1
            ? "Please enter your details to delete your account"
            : "Please enter the OTP to confirm account deletion"
        }
      />
      <div className="mt-4 px-6">
        {step === 1 ? renderStepOne() : renderStepTwo()}
      </div>
    </div>
  ) : (
    <Layout className="bg-slate-100 min-h-screen w-full">
      <div className="min-h-screen bg-white 2xl:px-4 2xl:mx-auto 2xl:w-[1170px]">
        <div className="px-4 mx-auto lg:w-3/5 w-full">
          <div className="m-0 p-4 bg-slate-100 2xl:h-[100px] h-[100px] md:h-[80px]">
            <div className="2xl:text-3xl font-bold text-xl">Delete</div>
            <div className="2xl:text-sm font-bold text-xs">
              {step === 1
                ? "Please enter your details to delete your account"
                : "Please enter the OTP to confirm account deletion"}
            </div>
          </div>

          <div className="mt-4 relative">
            {step === 1 ? renderStepOne() : renderStepTwo()}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AccountDeleteModalTwo;
